import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SelfieSlide.css';
import logger from '../services/logger';
import { detectFace } from '../services/faceDetectionService';

function SelfieSlide({ onNext, onBack, initialSelfie }) {
  const { t } = useTranslation('selfie');
  const { t: tc } = useTranslation('common');

  const [hasPhoto, setHasPhoto] = useState(!!initialSelfie);
  const [photoBase64, setPhotoBase64] = useState(initialSelfie || '');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Démarrer la caméra au montage du composant
    if (!hasPhoto) {
      startCamera();
    }

    // Nettoyer le stream à la destruction du composant
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPhoto]);

  // Attacher le stream à la vidéo quand elle est montée
  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      logger.log('📺 Attachement du stream à l\'élément vidéo');
      videoRef.current.srcObject = streamRef.current;

      videoRef.current.onloadedmetadata = async () => {
        logger.log('✅ Métadonnées chargées');
        logger.log('📐 Dimensions vidéo:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);

        try {
          await videoRef.current.play();
          logger.log('✅ Lecture démarrée');
        } catch (err) {
          logger.error('❌ Erreur lors du démarrage de la vidéo:', err);
        }
      };
    }
  }, [isCameraActive]);

  const startCamera = async () => {
    try {
      setCameraError('');
      logger.log('🎥 Demande d\'accès à la caméra...');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      logger.log('✅ Stream obtenu:', stream);
      logger.log('📹 Video tracks:', stream.getVideoTracks());

      // Sauvegarder le stream immédiatement
      streamRef.current = stream;

      // Activer la caméra pour que l'élément vidéo soit rendu dans le DOM
      setIsCameraActive(true);

    } catch (error) {
      logger.error('❌ Erreur d\'accès à la caméra:', error);
      setCameraError(t('camera.error'));
    }
  };

  const stopCamera = () => {
    logger.log('🛑 Arrêt de la caméra...');

    // Arrêter tous les tracks du stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        logger.log('🔴 Arrêt du track:', track.kind, track.label, 'État:', track.readyState);
        track.stop();
        logger.log('   → Nouvel état:', track.readyState);
      });
      streamRef.current = null;
    }

    // Nettoyer l'élément vidéo
    if (videoRef.current) {
      logger.log('🧹 Nettoyage de l\'élément vidéo');
      const oldStream = videoRef.current.srcObject;
      if (oldStream) {
        oldStream.getTracks().forEach(track => {
          logger.log('🔴 Arrêt du track depuis vidéo:', track.kind, track.readyState);
          track.stop();
        });
      }
      videoRef.current.srcObject = null;
      videoRef.current.pause();
      videoRef.current.load(); // Force le nettoyage complet
    }

    setIsCameraActive(false);

    // Vérification finale de tous les streams actifs
    navigator.mediaDevices.enumerateDevices().then(() => {
      logger.log('✅ Caméra arrêtée et nettoyée');
      logger.log('🔍 Vérification : Y a-t-il encore des streams actifs ?');

      // Cette ligne devrait retourner un tableau vide ou des tracks tous "ended"
      if (streamRef.current) {
        logger.warn('⚠️ streamRef.current existe encore !');
      } else {
        logger.log('✓ streamRef.current est null');
      }
    });
  };

  const validateImage = async (base64Image) => {
    setIsValidating(true);
    setValidationError('');

    try {
      const result = await detectFace(base64Image);

      if (!result.has_face) {
        setValidationError(t('validation.noFace'));
        setHasPhoto(false);
        setPhotoBase64('');
        return false;
      }

      return true;
    } catch (error) {
      logger.error('❌ Erreur validation image:', error);
      setValidationError(t('validation.error'));
      setHasPhoto(false);
      setPhotoBase64('');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video && canvas) {
      // Définir les dimensions du canvas
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Dessiner l'image de la vidéo sur le canvas (miroir horizontal)
      const context = canvas.getContext('2d');
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convertir en base64
      const base64Image = canvas.toDataURL('image/jpeg', 0.8);

      // Arrêter la caméra
      stopCamera();

      // Afficher la preview pendant la validation
      setPhotoBase64(base64Image);
      setHasPhoto(true);

      // Valider la présence d'un visage
      const isValid = await validateImage(base64Image);
      if (!isValid) {
        startCamera();
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Image = e.target.result;

      stopCamera();

      // Afficher la preview pendant la validation
      setPhotoBase64(base64Image);
      setHasPhoto(true);

      // Valider la présence d'un visage
      const isValid = await validateImage(base64Image);
      if (!isValid) {
        startCamera();
      }
    };
    reader.readAsDataURL(file);

    // Reset l'input pour pouvoir re-sélectionner le même fichier
    event.target.value = '';
  };

  const retakePhoto = () => {
    setHasPhoto(false);
    setPhotoBase64('');
    startCamera();
  };

  const isValidated = hasPhoto && !isValidating;

  const handleNext = () => {
    if (hasPhoto && photoBase64) {
      onNext({ selfieBase64: photoBase64 });
    }
  };

  const handleSkip = () => {
    stopCamera();
    onNext({ selfieBase64: null, selfieSkipped: true });
  };

  return (
    <div className="slide selfie-slide">
      <div className="slide-content">
        <h2>{t('title')}</h2>
        <p className="slide-description">
          {hasPhoto ? t('description.ready') : t('description.instructions')}
        </p>

        {cameraError && (
          <div className="camera-error">
            <p>{cameraError}</p>
            <button className="btn btn-secondary" onClick={startCamera}>
              {tc('buttons.retry')}
            </button>
          </div>
        )}

        {validationError && (
          <div className="camera-error">
            <p>{validationError}</p>
          </div>
        )}

        {isValidating && (
          <div className="camera-loading">
            <p>{t('validation.checking')}</p>
          </div>
        )}

        <div className="camera-container">
          {!hasPhoto && isCameraActive && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-video"
              style={{ transform: 'scaleX(-1)' }}
            />
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {hasPhoto && photoBase64 && (
            <div className="photo-preview">
              <img src={photoBase64} alt={t('altText')} />
            </div>
          )}

          {!hasPhoto && !isCameraActive && !cameraError && (
            <div className="camera-loading">
              <p>{t('camera.loading')}</p>
            </div>
          )}
        </div>

        <div className="camera-controls">
          {!hasPhoto && isCameraActive && (
            <button className="btn btn-camera" onClick={capturePhoto}>
              {t('buttons.capture')}
            </button>
          )}

          {!hasPhoto && (
            <button className="btn btn-gallery" onClick={() => fileInputRef.current?.click()}>
              {t('buttons.import')}
            </button>
          )}

          {hasPhoto && (
            <button className="btn btn-secondary" onClick={retakePhoto}>
              {t('buttons.retake')}
            </button>
          )}
        </div>

        <div className="navigation-buttons">
          <button className="btn btn-secondary" onClick={onBack}>
            {tc('buttons.back')}
          </button>
          {isValidated ? (
            <button className="btn btn-primary" onClick={handleNext}>
              {tc('buttons.next')}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleSkip}>
              {tc('buttons.skip')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SelfieSlide;
